import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { firestore } from '../firebase';
import './HomeDzTab.css';

const HIGHLIGHT_DZS = ['skydive perris', 'skydive elsinore'];

const EXAMPLE_FORMAT = `[
  { "raw": "sd perris", "name": "Skydive Perris", "state": "CA", "country": "US" },
  { "raw": "elsinore", "name": "Skydive Elsinore", "state": "CA", "country": "US" }
]`;

/** State/country from registration (Fury import); same display rules as DZ mapping. */
function geoLabelFromRegistration(f) {
  const country = String(f.country || '').trim().toUpperCase();
  const state = String(f.state || '').trim().toUpperCase();
  if (country && country !== 'US') return country;
  if (country === 'US' && state) return state;
  if (state) return state;
  return null;
}

function geoLabelFromMapping(m) {
  if (!m) return null;
  const country = String(m.country || '').trim().toUpperCase();
  const state = String(m.state || '').trim().toUpperCase();
  if (country && country !== 'US') return country;
  return state || null;
}

function stateCountryLabelForFlyer(f, lookupMapping) {
  const fromReg = geoLabelFromRegistration(f);
  if (fromReg) return fromReg;
  const raw = (f.homeDz || '').trim();
  if (!raw) return null;
  const m = lookupMapping(raw);
  if (!m) return 'Unmapped';
  return geoLabelFromMapping(m) || 'Unknown';
}

/** For a raw DZ row: show registration geo tag only if all flyers with that raw agree. */
function unanimousRegistrationGeoLabel(raw, freshlingList) {
  const labels = [
    ...new Set(
      freshlingList
        .filter((f) => (f.homeDz || '').trim() === raw)
        .map(geoLabelFromRegistration)
        .filter(Boolean)
    ),
  ];
  return labels.length === 1 ? labels[0] : null;
}

function HomeDzTab({ flyers, selectedEvent }) {
  const [mappings, setMappings] = useState({});
  const [loadingMappings, setLoadingMappings] = useState(true);
  const [pasteText, setPasteText] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [parseError, setParseError] = useState('');
  const [saving, setSaving] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState('');
  const [exampleCopied, setExampleCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingMappings(true);
      try {
        const snap = await getDoc(doc(firestore, '_config', 'dzMappings'));
        if (!cancelled) {
          setMappings(snap.exists() ? (snap.data().mappings || {}) : {});
        }
      } catch {
        if (!cancelled) setMappings({});
      } finally {
        if (!cancelled) setLoadingMappings(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const freshlings = useMemo(
    () => flyers.filter((f) => f.type === 'freshling' && !f.test && !f.cancelled),
    [flyers]
  );

  const rawDzCounts = useMemo(() => {
    const counts = new Map();
    freshlings.forEach((f) => {
      const raw = (f.homeDz || '').trim();
      if (!raw) return;
      counts.set(raw, (counts.get(raw) || 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([raw, count]) => ({ raw, count }))
      .sort((a, b) => b.count - a.count || a.raw.localeCompare(b.raw));
  }, [freshlings]);

  const lookupMapping = useCallback(
    (raw) => mappings[(raw || '').trim().toLowerCase()] || null,
    [mappings]
  );

  const unmappedDzs = useMemo(
    () => rawDzCounts.filter((d) => !lookupMapping(d.raw)),
    [rawDzCounts, lookupMapping]
  );

  const withoutHomeDzCount = useMemo(
    () => freshlings.filter((f) => !(f.homeDz || '').trim()).length,
    [freshlings]
  );

  // --- Breakdowns (only mapped entries) ---
  const dzBreakdown = useMemo(() => {
    const counts = new Map();
    freshlings.forEach((f) => {
      const raw = (f.homeDz || '').trim();
      if (!raw) return;
      const m = lookupMapping(raw);
      const name = m ? m.name : raw;
      counts.set(name, (counts.get(name) || 0) + 1);
    });
    const total = freshlings.filter((f) => (f.homeDz || '').trim()).length;
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count, pct: total ? ((count / total) * 100).toFixed(1) : '0.0' }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [freshlings, lookupMapping]);

  const stateBreakdown = useMemo(() => {
    const counts = new Map();
    freshlings.forEach((f) => {
      const raw = (f.homeDz || '').trim();
      if (!raw) return;
      const label = stateCountryLabelForFlyer(f, lookupMapping);
      if (label == null) return;
      counts.set(label, (counts.get(label) || 0) + 1);
    });
    const total = freshlings.filter((f) => (f.homeDz || '').trim()).length;
    return Array.from(counts.entries())
      .map(([label, count]) => ({ label, count, pct: total ? ((count / total) * 100).toFixed(1) : '0.0' }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }, [freshlings, lookupMapping]);

  // --- Clipboard ---
  const copyToClipboard = async (mode) => {
    const items = mode === 'unmapped'
      ? unmappedDzs.map((d) => d.raw)
      : rawDzCounts.map((d) => d.raw);
    if (items.length === 0) {
      setCopyFeedback('Nothing to copy');
      setTimeout(() => setCopyFeedback(''), 2000);
      return;
    }
    try {
      await navigator.clipboard.writeText(items.join('\n'));
      setCopyFeedback(`Copied ${items.length} value${items.length === 1 ? '' : 's'}`);
    } catch {
      setCopyFeedback('Copy failed');
    }
    setTimeout(() => setCopyFeedback(''), 2000);
  };

  // --- Parse / Preview / Save ---
  const handlePreview = () => {
    setParseError('');
    setPreviewData(null);
    const text = pasteText.trim();
    if (!text) {
      setParseError('Paste a JSON array first.');
      return;
    }
    try {
      const parsed = JSON.parse(text);
      if (!Array.isArray(parsed)) {
        setParseError('Expected a JSON array.');
        return;
      }
      const entries = parsed.map((item, i) => {
        const raw = (item.raw || '').trim();
        if (!raw) throw new Error(`Entry ${i + 1} is missing "raw" field.`);
        return {
          key: raw.toLowerCase(),
          raw,
          name: (item.name || '').trim() || raw,
          state: (item.state || '').trim(),
          country: (item.country || '').trim() || 'US',
        };
      });
      setPreviewData(entries);
    } catch (err) {
      setParseError(err.message);
    }
  };

  const handleSave = async () => {
    if (!previewData || previewData.length === 0) return;
    setSaving(true);
    try {
      const merged = { ...mappings };
      previewData.forEach((entry) => {
        merged[entry.key] = { name: entry.name, state: entry.state, country: entry.country };
      });
      await setDoc(doc(firestore, '_config', 'dzMappings'), { mappings: merged });
      setMappings(merged);
      setPreviewData(null);
      setPasteText('');
    } catch (err) {
      setParseError('Save failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const isHighlighted = (name) =>
    HIGHLIGHT_DZS.includes((name || '').toLowerCase());

  if (!selectedEvent) {
    return <div className="homedz-tab"><p>Select an event first.</p></div>;
  }

  return (
    <div className="homedz-tab">
      {/* Summary stats */}
      <div className="homedz-stats">
        <span><strong>{freshlings.length}</strong> freshlings</span>
        <span><strong>{rawDzCounts.length}</strong> unique DZs</span>
        <span className={unmappedDzs.length > 0 ? 'homedz-stat-warn' : ''}>
          <strong>{unmappedDzs.length}</strong> unmapped
        </span>
        <span><strong>{withoutHomeDzCount}</strong> no DZ entered</span>
      </div>

      {/* Section 1: Raw DZ list */}
      <div className="homedz-section">
        <div className="homedz-section-header">
          <h3>Raw Home DZ Values</h3>
          <div className="homedz-copy-buttons">
            <button onClick={() => copyToClipboard('unmapped')} disabled={unmappedDzs.length === 0}>
              Copy Unmapped ({unmappedDzs.length})
            </button>
            <button onClick={() => copyToClipboard('all')} disabled={rawDzCounts.length === 0}>
              Copy All ({rawDzCounts.length})
            </button>
            {copyFeedback && <span className="homedz-copy-feedback">{copyFeedback}</span>}
          </div>
        </div>
        {loadingMappings ? (
          <p>Loading mappings...</p>
        ) : rawDzCounts.length === 0 ? (
          <p className="homedz-empty">No home DZ values found for freshlings in this event.</p>
        ) : (
          <table className="homedz-raw-table">
            <thead>
              <tr>
                <th>Raw Value</th>
                <th>Count</th>
                <th>Mapped To</th>
              </tr>
            </thead>
            <tbody>
              {rawDzCounts.map((d) => {
                const m = lookupMapping(d.raw);
                const regTag = unanimousRegistrationGeoLabel(d.raw, freshlings);
                const mapTag = m ? geoLabelFromMapping(m) : null;
                const displayTag = regTag || mapTag;
                return (
                  <tr key={d.raw} className={m && isHighlighted(m.name) ? 'homedz-row-highlight' : ''}>
                    <td>{d.raw}</td>
                    <td className="homedz-num">{d.count}</td>
                    <td>
                      {m ? (
                        <span className="homedz-mapped">
                          {m.name}
                          {displayTag && <span className="homedz-state-tag">{displayTag}</span>}
                        </span>
                      ) : (
                        <span className="homedz-unmapped-badge">Unmapped</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Section 2: Paste mapping */}
      <div className="homedz-section">
        <h3>Import DZ Mapping</h3>
        <div className="homedz-example">
          <div className="homedz-example-header">
            <span className="homedz-example-label">Expected JSON format</span>
            <button
              className="homedz-example-copy"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(EXAMPLE_FORMAT);
                  setExampleCopied(true);
                  setTimeout(() => setExampleCopied(false), 2000);
                } catch {}
              }}
            >
              {exampleCopied ? 'Copied' : 'Copy example'}
            </button>
          </div>
          <pre className="homedz-example-code">{EXAMPLE_FORMAT}</pre>
        </div>
        <textarea
          className="homedz-paste-area"
          rows={8}
          value={pasteText}
          onChange={(e) => { setPasteText(e.target.value); setPreviewData(null); setParseError(''); }}
          placeholder="Paste JSON mapping here..."
        />
        <div className="homedz-paste-actions">
          <button onClick={handlePreview} disabled={!pasteText.trim()}>Preview</button>
          {parseError && <span className="homedz-parse-error">{parseError}</span>}
        </div>
        {previewData && previewData.length > 0 && (
          <div className="homedz-preview">
            <table className="homedz-preview-table">
              <thead>
                <tr>
                  <th>Raw</th>
                  <th>Normalized Name</th>
                  <th>State</th>
                  <th>Country</th>
                </tr>
              </thead>
              <tbody>
                {previewData.map((entry) => (
                  <tr key={entry.key}>
                    <td>{entry.raw}</td>
                    <td>{entry.name}</td>
                    <td>{entry.state || '—'}</td>
                    <td>{entry.country}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="homedz-save-row">
              <button onClick={handleSave} disabled={saving} className="homedz-save-btn">
                {saving ? 'Saving...' : `Save ${previewData.length} Mapping${previewData.length === 1 ? '' : 's'}`}
              </button>
              <span className="homedz-save-note">
                Merges with existing mappings (won't remove old ones).
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Section 3: Breakdowns */}
      {rawDzCounts.length > 0 && (
        <div className="homedz-breakdowns">
          <div className="homedz-breakdown-col">
            <h3>By Dropzone</h3>
            <table className="homedz-breakdown-table">
              <thead>
                <tr>
                  <th>Dropzone</th>
                  <th>Count</th>
                  <th>%</th>
                </tr>
              </thead>
              <tbody>
                {dzBreakdown.map((d) => (
                  <tr key={d.name} className={isHighlighted(d.name) ? 'homedz-row-highlight' : ''}>
                    <td>{d.name}</td>
                    <td className="homedz-num">{d.count}</td>
                    <td className="homedz-num">{d.pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="homedz-breakdown-col">
            <h3>By State / Country</h3>
            <table className="homedz-breakdown-table">
              <thead>
                <tr>
                  <th>State / Country</th>
                  <th>Count</th>
                  <th>%</th>
                </tr>
              </thead>
              <tbody>
                {stateBreakdown.map((d) => (
                  <tr key={d.label} className={d.label === 'Unmapped' ? 'homedz-row-unmapped' : ''}>
                    <td>{d.label}</td>
                    <td className="homedz-num">{d.count}</td>
                    <td className="homedz-num">{d.pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default HomeDzTab;
